import { ButtonType, ButtonWidget, FlyoutListMenuWidget, MenuButtonWidget, SelectedToggle, ToggleTextWidget } from '@tableau/widgets';
import React from 'react';
import * as TypeStyle from 'typestyle';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { SettingsCoordinator } from '~settings-coordinator/SettingsCoordinator';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { Icons } from '~src/commonInfra/components/Icons';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { fetchResourceActions } from '~src/resourceActions/actions/ResourceActions';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { coordinatorSubmitFailedHandler, coordinatorSubmitHandler } from '~src/settings/general-settings/components/common/GeneralToastHandlers';
import { ISiteAdminSettings } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { Sites } from '~src/siteManagement/services/resources/Sites';

import { IPackage } from '../models/IPackage';
import {
  PACKAGES_EMPTY_TEST_ID,
  PACKAGES_ROW_ACTIONS_TEST_ID,
  PACKAGES_SECTION_TEST_ID,
  PACKAGES_TAB_TOGGLE_TEST_ID,
  PACKAGES_UPLOAD_BUTTON_TEST_ID,
} from '../PackagesTestIds';
import { DeletePackageService } from '../serverApi/requests/DeletePackage';
import { ListPackagesService } from '../serverApi/requests/ListPackages';
import { SetPackageDefaultActivationService } from '../serverApi/requests/SetPackageDefaultActivation';
import { SetPackageEnabledService } from '../serverApi/requests/SetPackageEnabled';
import { PackagesSettings } from './PackagesSettings';
import { UploadPackageDialog } from './UploadPackageDialog';

// Site-admin section for managing uploaded .tpkg packages. Each row exposes
// two independent toggles: Enabled (kill-switch) and Default activation
// (on-by-default policy). See analysis/packages-sharing-and-state-model.md.
//
// Phase 1: standalone list with hand-rolled table, local fetch, and an inline
// action menu — matching the convention of other admin settings tables. When
// Packages become permissioned and appear in cross-entity lists, this will
// move to the Explorer / IEntityAction pipeline. The TODOs in
// SortOptionDefinitions.ts and SiteUserSpaceProvider.tsx flag the related
// branches to revisit at that point.

// Package UI actions the backend maps to Package capabilities via the
// getAllowedResourceActionsForActor pipeline.
const PACKAGE_CONFIGURE_ACTIONS = ['CONFIGURE_PACKAGE'];
const PACKAGE_MODIFY_ACTIONS = ['MODIFY_PACKAGE'];

interface PackageCapabilities {
  canConfigure: boolean;
  canModify: boolean;
}

function readPackageCapabilities(): PackageCapabilities {
  const service = getSessionManagementServiceInstance();
  return {
    canConfigure: service.canSessionManageUsersOrActOnResource('Package', PACKAGE_CONFIGURE_ACTIONS),
    canModify: service.canSessionManageUsersOrActOnResource('Package', PACKAGE_MODIFY_ACTIONS),
  };
}

// Fetches the actor's Package resource-actions on mount and re-reads them when
// they arrive. The section is not Redux-connected (Phase 1 reads the store
// directly), so it subscribes to keep capability-gated UI in sync once the
// getAllowedResourceActionsForActor response lands.
function usePackageCapabilities(): PackageCapabilities {
  const [capabilities, setCapabilities] = React.useState<PackageCapabilities>(readPackageCapabilities);
  React.useEffect(() => {
    const store = getAppStore();
    // Resource actions are only read when admin profiles is enabled, and the endpoint rejects calls with the gate off (W-24458049).
    if (getSessionManagementServiceInstance().isAdminProfilesEnabled()) {
      store.dispatch(fetchResourceActions());
    }
    const recompute = () => setCapabilities(prev => {
      const next = readPackageCapabilities();
      return prev.canConfigure === next.canConfigure && prev.canModify === next.canModify ? prev : next;
    });
    recompute();
    return store.subscribe(recompute);
  }, []);
  return capabilities;
}

function usePackagesAdminEnabled(): boolean {
  const [enabled, setEnabled] = React.useState(true);
  React.useEffect(() => {
    Sites.instance.getCurrentSiteSettingsForSiteAdmin()
      .then(result => {
        const settings = result.siteAdminSettings as ISiteAdminSettings;
        setEnabled(settings.siteSettingsV2?.packagesEnabled === true);
      })
      .catch(() => undefined);
  }, []);
  return enabled;
}

export function PackagesSiteSection(): React.ReactElement | null {
  const [selectedToggle, setSelectedToggle] = React.useState<SelectedToggle>(SelectedToggle.Left);
  const [packages, setPackages] = React.useState<IPackage[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const { canConfigure, canModify } = usePackageCapabilities();
  const packagesAdminEnabled = usePackagesAdminEnabled();

  const loadPackages = React.useCallback(() => {
    setPackages(null);
    setLoadError(null);
    return ListPackagesService.listPackages()
      .then(response => {
        setPackages(response.packages?.package ?? []);
      })
      .catch((err: { data?: { error?: { detail?: string } } }) => {
        setLoadError(err?.data?.error?.detail ?? 'Failed to load packages');
      });
  }, []);

  React.useEffect(() => { loadPackages(); }, [loadPackages]);

  const openUploadDialog = React.useCallback((targetPackage?: IPackage) => {
    const modal = ReactModalService.openCustom<boolean>(closeAndDismiss => (
      <UploadPackageDialog
        close={closeAndDismiss.close}
        dismiss={closeAndDismiss.dismiss}
        targetPackage={targetPackage} />
    ));
    modal.result.then(success => {
      if (success) {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: targetPackage ? Messages.packages_update_success() : Messages.packages_upload_success(),
        });
        loadPackages();
      }
    }, () => undefined);
  }, [loadPackages]);

  const handleToggleEnabled = React.useCallback((pkg: IPackage) => {
    if (!pkg.luid) {
      return;
    }
    const next = !pkg.enabled;
    SetPackageEnabledService.setPackageEnabled(pkg.luid, next)
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.packages_setEnabled_success(),
        });
        loadPackages();
      })
      .catch(() => {
        ToasterService.instance.pop({
          type: ToastType.Error,
          message: Messages.packages_setEnabled_error(),
        });
      });
  }, [loadPackages]);

  const handleToggleDefaultActivation = React.useCallback((pkg: IPackage) => {
    if (!pkg.luid) {
      return;
    }
    const next = !pkg.defaultActivation;
    SetPackageDefaultActivationService.setPackageDefaultActivation(pkg.luid, next)
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.packages_setDefaultActivation_success(),
        });
        loadPackages();
      })
      .catch(() => {
        ToasterService.instance.pop({
          type: ToastType.Error,
          message: Messages.packages_setDefaultActivation_error(),
        });
      });
  }, [loadPackages]);

  const handleUpdate = React.useCallback((pkg: IPackage) => {
    openUploadDialog(pkg);
  }, [openUploadDialog]);

  // TODO: open sharing dialog once non-owner grants ship.
  const handleShare = React.useCallback((_pkg: IPackage) => undefined, []);

  const handleDelete = React.useCallback((pkg: IPackage) => {
    if (!pkg.luid) {
      return;
    }
    const luid = pkg.luid;
    openReactConfirmationDialog<string, void>({
      title: Messages.packages_delete_dialog_title(),
      message: Messages.packages_delete_dialog_message(),
      actionName: Messages.packages_actions_delete(),
      isActionDelete: true,
      paramsForAction: luid,
      action: (id: string) => DeletePackageService.deletePackage(id),
      dialogWidth: TabDialogWidth.width500px,
    }).result.then(() => {
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.packages_delete_success(),
      });
      loadPackages();
    }, () => undefined);
  }, [loadPackages]);

  return (
    <SettingsCoordinator
      onSubmit={coordinatorSubmitHandler}
      onSubmitFailure={coordinatorSubmitFailedHandler}
      allowFullWidthSections
      submitSequentially
      hideBottomSaveButtons>
      <div data-tb-test-id={PACKAGES_SECTION_TEST_ID} style={{ marginBottom: 16 }}>
        <ToggleTextWidget
          ariaLabel={Messages.packages_header()}
          containerStyle={{ width: 220 }}
          handleLeftToggleSelected={() => setSelectedToggle(SelectedToggle.Left)}
          handleRightToggleSelected={() => setSelectedToggle(SelectedToggle.Right)}
          leftToggleText={Messages.packages_toggle_settings()}
          rightToggleText={Messages.packages_toggle_packages()}
          selectedToggle={selectedToggle}
          testId={PACKAGES_TAB_TOGGLE_TEST_ID}
        />
      </div>
      {selectedToggle === SelectedToggle.Left ? (
        <PackagesSettings />
      ) : (
        <>
          {canModify && FeatureFlags.independentPackagesEnabled && (
            <div style={{ marginBottom: 12 }}>
              <ButtonWidget
                buttonType={ButtonType.Primary}
                children={Messages.packages_uploadButton()}
                handleClick={() => openUploadDialog()}
                testId={PACKAGES_UPLOAD_BUTTON_TEST_ID} />
            </div>
          )}
          <PackagesTable
            packages={packages}
            loadError={loadError}
            canConfigure={canConfigure}
            canModify={canModify}
            actionsDisabled={!packagesAdminEnabled}
            onToggleEnabled={handleToggleEnabled}
            onToggleDefaultActivation={handleToggleDefaultActivation}
            onShare={handleShare}
            onUpdate={handleUpdate}
            onDelete={handleDelete} />
        </>
      )}
    </SettingsCoordinator>
  );
}

interface PackagesTableProps {
  packages: IPackage[] | null;
  loadError: string | null;
  canConfigure: boolean;
  canModify: boolean;
  actionsDisabled: boolean;
  onToggleEnabled: (pkg: IPackage) => void;
  onToggleDefaultActivation: (pkg: IPackage) => void;
  onShare: (pkg: IPackage) => void;
  onUpdate: (pkg: IPackage) => void;
  onDelete: (pkg: IPackage) => void;
}

function PackagesTable(props: PackagesTableProps) {
  const { packages, loadError, canConfigure, canModify, actionsDisabled, onToggleEnabled, onToggleDefaultActivation, onShare, onUpdate, onDelete } = props;

  if (loadError) {
    return <div className="tb-warning-text">{loadError}</div>;
  }

  if (packages === null) {
    return <div>{'Loading...'}</div>;
  }

  if (packages.length === 0) {
    return (
      <div data-tb-test-id={PACKAGES_EMPTY_TEST_ID}>
        {Messages.packages_empty()}
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={cellStyle}>{Messages.packages_columnHeader_name()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_actions()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_workbook()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_packageId()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_version()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_author()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_enabled()}</th>
          {canConfigure && <th style={cellStyle}>{Messages.packages_columnHeader_defaultActivation()}</th>}
          <th style={cellStyle}>{Messages.packages_columnHeader_extensions()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_uploaded()}</th>
        </tr>
      </thead>
      <tbody>
        {packages.map(pkg => (
          <tr key={pkg.luid}>
            <td style={cellStyle}>
              <PackageNameLink pkg={pkg} />
            </td>
            <td style={cellStyle}>
              <PackageRowActions
                pkg={pkg}
                canConfigure={canConfigure}
                canModify={canModify}
                actionsDisabled={actionsDisabled}
                onToggleEnabled={onToggleEnabled}
                onToggleDefaultActivation={onToggleDefaultActivation}
                onShare={onShare}
                onUpdate={onUpdate}
                onDelete={onDelete} />
            </td>
            <td style={cellStyle}>{pkg.workbookName ?? '-'}</td>
            <td style={cellStyle}>{pkg.manifestId}</td>
            <td style={cellStyle}>{pkg.version}</td>
            <td style={cellStyle}>{pkg.author}</td>
            <td style={cellStyle}>{enabledLabel(pkg)}</td>
            {canConfigure && <td style={cellStyle}>{defaultActivationLabel(pkg)}</td>}
            <td style={cellStyle}>{pkg.extensions?.extension?.length ?? 0}</td>
            <td style={cellStyle}>{pkg.createdAt}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function enabledLabel(pkg: IPackage): string {
  return pkg.enabled
    ? Messages.packages_state_on()
    : Messages.packages_state_off();
}

function defaultActivationLabel(pkg: IPackage): string {
  return pkg.defaultActivation
    ? Messages.packages_state_on()
    : Messages.packages_state_off();
}

function PackageRowActions(props: {
  pkg: IPackage;
  canConfigure: boolean;
  canModify: boolean;
  actionsDisabled: boolean;
  onToggleEnabled: (pkg: IPackage) => void;
  onToggleDefaultActivation: (pkg: IPackage) => void;
  onShare: (pkg: IPackage) => void;
  onUpdate: (pkg: IPackage) => void;
  onDelete: (pkg: IPackage) => void;
}): JSX.Element {
  const { pkg, canConfigure, canModify, actionsDisabled, onToggleEnabled, onToggleDefaultActivation, onShare, onUpdate, onDelete } = props;
  const buttonRef = React.useRef<HTMLButtonElement>();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen(o => !o), []);

  const handleSelect = React.useCallback((value: string) => {
    setIsOpen(false);
    if (value === 'toggleEnabled') {
      onToggleEnabled(pkg);
    } else if (value === 'toggleDefaultActivation') {
      onToggleDefaultActivation(pkg);
    } else if (value === 'share') {
      onShare(pkg);
    } else if (value === 'update') {
      onUpdate(pkg);
    } else if (value === 'delete') {
      onDelete(pkg);
    }
  }, [pkg, onToggleEnabled, onToggleDefaultActivation, onShare, onUpdate, onDelete]);

  const isEnabled = !!pkg.enabled;

  // Enable/Disable and Toggle default activation require Package:configure;
  // Share, Update and Delete require Package:modify. Unauthorized items are
  // omitted from the menu (hidden, not shown disabled). When the actor may
  // perform no action at all, keep the trigger and show a single disabled
  // 'None' item rather than dropping the row's action affordance entirely.
  // When packagesEnabled admin setting is off, all actions are suppressed.
  const menuItemInfos: Array<{ value: string, displayValue: string, disabled?: boolean }> = [];
  if (!actionsDisabled && pkg.workbookLuid) {
    // Workbook-embedded packages only support Enable/Disable; the other actions apply to
    // standalone packages only.
    if (canConfigure) {
      menuItemInfos.push({
        value: 'toggleEnabled',
        displayValue: isEnabled
          ? Messages.packages_actions_setEnabledOff()
          : Messages.packages_actions_setEnabledOn(),
      });
    }
  } else if (!actionsDisabled) {
    if (canConfigure) {
      menuItemInfos.push({
        value: 'toggleEnabled',
        displayValue: isEnabled
          ? Messages.packages_actions_setEnabledOff()
          : Messages.packages_actions_setEnabledOn(),
      });
      menuItemInfos.push({
        value: 'toggleDefaultActivation',
        displayValue: Messages.packages_actions_toggleDefaultActivation(),
      });
    }
    if (canModify) {
      menuItemInfos.push({ value: 'share', displayValue: Messages.packages_actions_share() });
      menuItemInfos.push({ value: 'update', displayValue: Messages.packages_actions_update() });
      menuItemInfos.push({ value: 'delete', displayValue: Messages.packages_actions_delete() });
    }
  }

  // Nothing the actor may do on this package: keep the trigger but show a
  // single disabled 'None' placeholder so the row still reads as actionable-
  // in-principle and layout stays consistent with authorized rows.
  if (menuItemInfos.length === 0) {
    menuItemInfos.push({ value: 'none', displayValue: Messages.none(), disabled: true });
  }

  const flyoutId = `packages-row-actions-flyout-${pkg.luid ?? ''}`;
  const testId = `${PACKAGES_ROW_ACTIONS_TEST_ID}-${pkg.luid ?? ''}`;

  return (
    <span className={inlineBlockClass}>
      <MenuButtonWidget
        aria-controls={flyoutId}
        aria-label={Messages.moreActions()}
        className={moreActionsButtonStyleName}
        onClick={toggle}
        ref={buttonRef}
        testId={testId}>
        <Icons.actionIcon />
      </MenuButtonWidget>
      {isOpen && (
        <FlyoutListMenuWidget
          allowAutoPosition
          anchorEl={buttonRef.current}
          id={flyoutId}
          menuItemInfos={menuItemInfos}
          menuStyle={{ minWidth: 144 }}
          onSelect={handleSelect}
          requestCloseAll={close}
          testId={`${testId}-flyout`}
          toggleEl={buttonRef.current} />
      )}
    </span>
  );
}

function PackageNameLink(props: { pkg: IPackage }): JSX.Element {
  const { pkg } = props;
  const display = pkg.name ?? pkg.manifestId ?? '';
  if (!pkg.luid) {
    return <span>{display}</span>;
  }
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  if (!siteUrls) {
    return <span>{display}</span>;
  }
  return <a href={siteUrls.packageDetailsUrl(pkg.luid)}>{display}</a>;
}

const cellStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderBottom: '1px solid #ddd',
  textAlign: 'left',
};

const inlineBlockClass = TypeStyle.style({
  display: 'inline-block',
});

const moreActionsButtonStyleName = TypeStyle.style({
  border: 'none',
  verticalAlign: 'middle',
  $nest: {
    '&&': { height: 20, width: 20, minHeight: 20, minWidth: 20, padding: 0 },
    '&&::after': { display: 'none' },
  },
});
